import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
// import BasicInfo from './BasicInfo'; // Make sure BasicInfo handles onClose prop
import { assessmentAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import AssessmentModal from './AssessmentModal';

// Enhanced Skeleton Loader Components
const AssessmentCardSkeleton = () => (
  <div className="bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-2 animate-pulse">
    <div>
      {/* Patient Name Skeleton */}
      <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
      {/* Patient ID Skeleton */}
      <div className="h-3 bg-gray-200 rounded w-32 mb-2"></div>
      {/* Status Skeleton */}
      <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>
      {/* Progress Bar Skeleton */}
      <div className="w-full bg-gray-200 rounded-full h-1.5 my-2">
        <div className="bg-gray-300 h-1.5 rounded-full w-1/3"></div>
      </div>
      {/* Progress Text Skeleton */}
      <div className="h-3 bg-gray-200 rounded w-28 mb-2"></div>
      {/* Risk Skeleton */}
      <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
    </div>
    {/* Button Skeleton */}
    <div className="h-10 bg-gray-200 rounded-md mt-2"></div>
  </div>
);

const FiltersSkeleton = () => (
  <div className="mb-6 justify-between flex flex-wrap gap-3">
    {/* Search Input Skeleton */}
    <div className="relative">
      <div className="w-72 h-10 bg-gray-200 rounded-lg"></div>
    </div>
    
    {/* Filter Dropdowns Skeleton */}
    <div className="flex gap-4 flex-wrap">
      <div className="w-24 h-10 bg-gray-200 rounded-md"></div>
      <div className="w-20 h-10 bg-gray-200 rounded-md"></div>
      <div className="w-32 h-10 bg-gray-200 rounded-md"></div>
    </div>
  </div>
);

// Content Loader Component
const ContentLoader = () => (
  <div className="min-h-screen">
    <div className="mb-6">
      {/* Header Skeleton */}
      <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-96"></div>
    </div>

    {/* Filters Skeleton */}
    <FiltersSkeleton />

    {/* Assessment Cards Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[...Array(9)].map((_, idx) => (
        <AssessmentCardSkeleton key={idx} />
      ))}
    </div>

    {/* Pagination Skeleton */}
    <div className="flex justify-between items-center mt-8">
      <div className="h-4 bg-gray-200 rounded w-48"></div>
      <div className="flex gap-2">
        {[...Array(5)].map((_, idx) => (
          <div key={idx} className="w-8 h-8 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

const Assessments = () => {
    const [openModal, setOpenModal] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const navigate = useNavigate();
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(9);
    const [filteredAssessments, setFilteredAssessments] = useState([]);
    
    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [assessmentTypeFilter, setAssessmentTypeFilter] = useState('');

    // Define getProgress function before using it in useEffect
    const getProgress = (responses) => {
        const requiredSections = ['basicInfo', 'symptoms', 'impact', 'History', 'consent'];
        return requiredSections.filter(section => responses && responses[section]).length;
    };

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        const doctorId = user?._id || user?.id;
        if (!doctorId) return;
        
        setInitialLoading(true);
        
        // Add artificial delay for initial loading
        const loadData = async () => {
            try {
                await new Promise(resolve => setTimeout(resolve, 800));
                const res = await assessmentAPI.getAssessmentsForDoctor(doctorId);
                const allAssessments = res.assessments || [];
                setAssessments(allAssessments);
                setLoading(false);
                setInitialLoading(false);
            } catch (error) {
                setLoading(false);
                setInitialLoading(false);
            }
        };
        
        loadData();
    }, []);

    // Filter and paginate assessments
    useEffect(() => {
        setPaginationLoading(true);
        
        // Consistent delay for all loading states
        const timeoutId = setTimeout(() => {
            let filtered = [...assessments];

            // Apply search filter
            if (searchTerm) {
                filtered = filtered.filter(assessment => 
                    assessment.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    assessment.patient?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    assessment.patient?._id?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }

            // Apply status filter
            if (statusFilter) {
                filtered = filtered.filter(assessment => 
                    assessment.status === statusFilter
                );
            }

            // Apply assessment type filter
            if (assessmentTypeFilter) {
                filtered = filtered.filter(assessment => 
                    assessment.type === assessmentTypeFilter
                );
            }

            // Apply sorting
            if (sortBy) {
                filtered.sort((a, b) => {
                    switch (sortBy) {
                        case 'name':
                            return (a.patient?.name || '').localeCompare(b.patient?.name || '');
                        case 'date':
                            return new Date(b.createdAt) - new Date(a.createdAt);
                        case 'status':
                            return (a.status || '').localeCompare(b.status || '');
                        case 'progress':
                            return getProgress(b.responses) - getProgress(a.responses);
                        default:
                            return 0;
                    }
                });
            }

            setFilteredAssessments(filtered);
            setCurrentPage(1); // Reset to first page when filters change
            setPaginationLoading(false);
        }, 800); // Consistent 800ms delay

        return () => clearTimeout(timeoutId);
    }, [assessments, searchTerm, statusFilter, sortBy, assessmentTypeFilter]);

    // Pagination logic
    const totalPages = Math.ceil(filteredAssessments.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentAssessments = filteredAssessments.slice(startIndex, endIndex);

    const openAssessmentModal = async (assessment) => {
        try {
            // Fetch the full assessment data
            const response = await assessmentAPI.getAssessmentById(assessment._id);
            setSelectedAssessment(response.assessment);
            setOpenModal(true);
        } catch (error) {
            console.error('Error fetching assessment:', error);
        }
    };

    const closeAssessmentModal = () => {
        setOpenModal(false);
        setSelectedAssessment(null);
    };

    // Handle page change with loading state
    const handlePageChange = (newPage) => {
        if (newPage === currentPage) return; // Prevent unnecessary loading
        
        setPaginationLoading(true);
        setCurrentPage(newPage);
        
        // Consistent delay for pagination
        setTimeout(() => {
            setPaginationLoading(false);
        }, 800); // Consistent 800ms delay
    };

    // Show content loader during initial load
    if (initialLoading) {
        return <ContentLoader />;
    }

    return (
        <div className="min-h-screen">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-800">Patient Assessments</h1>
                <p className="text-gray-600 mt-1">
                    Manage and review patient assessments with progress and risk insights.
                </p>
            </div>

            {/* Filters */}
            <div className="mb-6 justify-between flex flex-wrap gap-3">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search by name or email"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-72 pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>

                <div className="flex gap-4 flex-wrap">
                    <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                        <option value="">All Status</option>
                        <option value="submitted">submitted</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="reviewed">Reviewed</option>
                    </select>
                    <select 
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                        <option value="">Sort By</option>
                        <option value="name">Name</option>
                        <option value="date">Date</option>
                        <option value="status">Status</option>
                        <option value="progress">Progress</option>
                    </select>
                    <select 
                        value={assessmentTypeFilter}
                        onChange={(e) => setAssessmentTypeFilter(e.target.value)}
                        className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                        <option value="">All Types</option>
                        <option value="initial">Initial</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="screening">Screening</option>
                    </select>
                </div>
            </div>

            {/* Cards with loading state */}
            {(loading || paginationLoading) ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, idx) => (
                        <AssessmentCardSkeleton key={idx} />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {currentAssessments.map((a) => (
                        <div key={a._id} className="bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-2">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-800">{a.patient?.name || 'Unknown'}</h3>
                                <p className="text-xs text-gray-500">{a.patient?._id || ''}</p>
                                <p>Status: <span className="font-medium">{a.status}</span></p>
                                {/* Progress Bar */}
                                <div className="w-full bg-gray-200 rounded-full h-1.5 my-2">
                                    <div
                                        className="bg-blue-500 h-1.5 rounded-full"
                                        style={{ width: `${(getProgress(a.responses) / 5) * 100}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-600 mb-1">
                                    {getProgress(a.responses) === 0
                                        ? 'Not Started'
                                        : `Step ${getProgress(a.responses)} of 5 Complete`}
                                </p>
                                <p>Risk: <span className="font-medium">{a.riskFlags?.[0]}</span></p>
                            </div>
                            <button
                                onClick={() => openAssessmentModal(a)}
                                className="mt-2 w-full bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 rounded-md"
                            >
                                Review Assessment
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {filteredAssessments.length > 0 && !loading && !paginationLoading && (
              <div className="flex flex-wrap justify-between items-center mt-8 text-sm text-gray-600">
                <div className="mb-2">
                  Showing {startIndex + 1}–{Math.min(endIndex, filteredAssessments.length)} of {filteredAssessments.length} assessments
                </div>
                <div className="flex items-center border rounded-md overflow-hidden">
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1 || paginationLoading}
                    className="px-3 py-1 hover:bg-gray-100 disabled:opacity-50"
                  >
                    &lt;
                  </button>
                  {Array.from({ length: totalPages }, (_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePageChange(idx + 1)}
                      disabled={paginationLoading}
                      className={`px-3 py-1 ${
                        currentPage === idx + 1
                          ? 'bg-blue-100 text-blue-600 font-medium'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages || paginationLoading}
                    className="px-3 py-1 hover:bg-gray-100 disabled:opacity-50"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}

            {/* No Results Message */}
            {filteredAssessments.length === 0 && !loading && !initialLoading && !paginationLoading && (
                <div className="text-center py-8">
                    <p className="text-gray-500 text-lg">No assessments found matching your criteria.</p>
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('');
                            setSortBy('');
                            setAssessmentTypeFilter('');
                        }}
                        className="mt-2 text-blue-500 hover:text-blue-600 text-sm"
                    >
                        Clear all filters
                    </button>
                </div>
            )}
            
            {/* Assessment Modal */}
            {openModal && selectedAssessment && (
                <AssessmentModal
                    assessment={selectedAssessment}
                    onClose={closeAssessmentModal}
                />
            )}
        </div>
    );
};

export default Assessments;
